import { FINANCIAL_GATE_MESSAGE } from './types';
import type { CgStatus } from './types';
import type { GateTask } from './queries';

/**
 * The financial gate, as the browser understands it.
 *
 * None of this enforces anything. cg_orders_guard_financial_gate refuses the
 * write, and it refuses it for a stale tab and a direct API call too. What
 * lives here is the part that stops somebody pressing a button that was always
 * going to fail — and, more usefully, tells them WHICH piece of work is
 * outstanding, which the database error cannot.
 */

/**
 * The two boundaries in the run-up to the PO.
 *
 * They were one status until 20261112000100, and both rules were expressed as
 * "is the order on THIS status". Splitting 'Order Placed' into "with the
 * client" and "approved, waiting on the money" made that phrasing unsafe: a
 * status dropdown taken from 'Order Placed' straight to 'PO Sent to ES' passes
 * OVER 'Client Approved' without ever being on it, so a rule watching for
 * departures from 'Client Approved' would never fire.
 *
 * Everything below therefore asks whether a move CROSSES a boundary, which is
 * what both rules always meant. The database asks the same question the same
 * way (cg_status_crosses_gate) — two guards on one rule must not be able to
 * disagree about what the rule is.
 */

/** The status an order may not leave without the client's signature. */
export const APPROVAL_STATUS = 'Order Placed';

/** The status an order may not leave with money outstanding. */
export const GATED_STATUS = 'Client Approved';

export { FINANCIAL_GATE_MESSAGE };

function rankOf(statusId: string, statuses: CgStatus[]): number | null {
  const found = statuses.find((status) => status.id === statusId);
  return found === undefined ? null : found.sort_order;
}

/**
 * Whether moving `from` → `to` takes an order past `gate`.
 *
 * An unrankable status — one cg_statuses does not know — crosses nothing. That
 * is the recoverable direction and it matches every other guard here: the
 * foreign key refuses a genuinely unknown status, and greying out a dropdown
 * because a lookup missed would be a block nobody could explain.
 */
export function crossesGate(from: string, to: string, gate: string, statuses: CgStatus[]): boolean {
  const fromRank = rankOf(from, statuses);
  const toRank = rankOf(to, statuses);
  const gateRank = rankOf(gate, statuses);
  if (fromRank === null || toRank === null || gateRank === null) {
    return false;
  }
  return fromRank <= gateRank && toRank > gateRank;
}

/** Whether an order has not yet passed a boundary — so the rule still applies. */
function atOrBefore(status: string, gate: string, statuses: CgStatus[]): boolean {
  const rank = rankOf(status, statuses);
  const gateRank = rankOf(gate, statuses);
  if (rank === null || gateRank === null) {
    return false;
  }
  return rank <= gateRank;
}

/**
 * Whether the gate is holding this order.
 *
 * True from the day the order is created until it clears, not only while it
 * sits on the gated status: the invoice work happens while the client is still
 * looking at proofs, and a checklist that appeared only after they signed
 * would hide the one part of the gate somebody can get ahead on.
 *
 * An order with NO gate tasks is clear. That is the subtle case and it matches
 * the database exactly: the gate only exists once a client has signed off, and
 * orders that never went through the review portal must not be frozen by a
 * rule that was never meant to reach them.
 */
export function gateBlocks(status: string, tasks: GateTask[], statuses: CgStatus[]): boolean {
  if (!atOrBefore(status, GATED_STATUS, statuses)) {
    return false;
  }
  return tasks.some((task) => !task.isCompleted);
}

/** The gate tasks still to do, for the banner's list. */
export function outstandingTasks(tasks: GateTask[]): GateTask[] {
  return tasks.filter((task) => !task.isCompleted);
}

/**
 * Which statuses the dropdown must refuse while the gate is shut.
 *
 * The ones that would take the order PAST the money, which is every status
 * after 'Client Approved' and no others. 'Client Approved' itself stays
 * available from 'Order Placed' — an unpaid invoice is no reason to stop
 * recording that the client signed.
 */
export function blockedByGate(
  status: string,
  tasks: GateTask[],
  statuses: CgStatus[],
): Set<string> {
  if (!gateBlocks(status, tasks, statuses)) {
    return new Set();
  }
  return new Set(
    statuses
      .filter((entry) => crossesGate(status, entry.id, GATED_STATUS, statuses))
      .map((entry) => entry.id),
  );
}

/**
 * Which statuses the dropdown must refuse while the client has not signed.
 *
 * Same shape as blockedByGate and deliberately separate from it. The financial
 * gate's block is liftable by a TAP admin with a reason; this one is not, and
 * two rules with different remedies must not share a function — the day
 * somebody loosens one, the other must not move with it. Since 20261112000100
 * they do not share a boundary either.
 *
 * `undefined` (still loading) blocks nothing. The database refuses regardless,
 * so an optimistic dropdown costs a refused click, while a pessimistic one
 * greys out every option each time a drawer opens.
 */
export function blockedByApproval(
  status: string,
  clientApproved: boolean | undefined,
  statuses: CgStatus[],
): Set<string> {
  if (clientApproved !== false) {
    return new Set();
  }
  return new Set(
    statuses
      .filter((entry) => crossesGate(status, entry.id, APPROVAL_STATUS, statuses))
      .map((entry) => entry.id),
  );
}

/**
 * Whether this order has already moved past the point a signature was needed.
 *
 * Not the same question as "is it approved". An order sitting in Sampling with
 * no certificate on file is past the gate and unsigned — which the gate itself
 * makes impossible for anything created in this system, so the honest reading
 * is that it arrived another way: imported from the tracker sheet, or moved
 * before the gate existed. The Client approval section needs to tell those two
 * cases apart, because "the client has not signed off yet, here is a review
 * link" is good advice on a new order and nonsense on a job that shipped in
 * July.
 *
 * Ranked rather than name-matched, and false for a status cg_statuses does not
 * know — the recoverable direction, matching crossesGate above.
 */
export function pastApprovalGate(status: string, statuses: CgStatus[]): boolean {
  const at = rankOf(status, statuses);
  const gate = rankOf(APPROVAL_STATUS, statuses);
  if (at === null || gate === null) {
    return false;
  }
  return at > gate;
}

/**
 * What the Client approval section has to say about one order.
 *
 * Three answers, never none — which is the whole reason this is a function
 * rather than a couple of ternaries in the markup. The section rendered blank
 * for months because the only thing in it was a button that returns null when
 * there is nothing to open, and nobody can spot a missing case in JSX the way
 * they can in a switch with three names in it.
 *
 *   'certificate'  the client signed; show it
 *   'awaiting'     not signed yet, and the order has not moved on — the review
 *                  link is the remedy and belongs on this page
 *   'imported'     no certificate, but the order is already past the gate.
 *                  The gate refuses that move, so this order did not come
 *                  through it: imported, or moved before the gate existed.
 */
export type ClientApprovalState = 'certificate' | 'awaiting' | 'imported';

export function clientApprovalState(
  status: string,
  hasCertificate: boolean,
  statuses: CgStatus[],
): ClientApprovalState {
  if (hasCertificate) {
    return 'certificate';
  }
  return pastApprovalGate(status, statuses) ? 'imported' : 'awaiting';
}

/** What is stopping an override from being submitted, or null. */
export function overrideProblem(reason: string, isAdmin: boolean): string | null {
  if (!isAdmin) {
    return 'Only a TAP admin can override the financial gate.';
  }
  if (reason.trim() === '') {
    // The database refuses this too. Said here so somebody is told before the
    // round trip, in words rather than as an error code — and because an
    // override with no explanation is indistinguishable, months later, from
    // the gate not working.
    return 'Give a reason for the override. It goes into the order’s audit trail.';
  }
  return null;
}

// ── The checklist ──────────────────────────────────────────────────────────

/**
 * One step of the financial gate, as cg_gate_state returns it.
 *
 * Read through the RPC rather than from public.tasks. The gate tasks live in a
 * project auto-created for the order, and a CG member who is not a member of
 * THAT project could not read them — fetchGateTasks swallows the permission
 * error to an empty list, so they saw no outstanding work, changed the status,
 * and were refused by a trigger citing tasks they had never been shown.
 */
export interface GateStep {
  slug: string;
  title: string;
  sort_order: number;
  is_completed: boolean;
  task_id: string | null;
}

/** The two slugs, named so nothing has to spell them twice. */
export const GATE_SHOPIFY = 'shopify_order';
export const GATE_INVOICE = 'invoice_paid';

/** Whether every step is done. Zero steps counts as done — see gateBlocks. */
export function gateComplete(steps: GateStep[]): boolean {
  return steps.every((step) => step.is_completed);
}

export function stepDone(steps: GateStep[], slug: string): boolean {
  return steps.find((step) => step.slug === slug)?.is_completed === true;
}

/**
 * What the order list should show instead of a raw error string.
 *
 * `financial_gate_incomplete` is a Postgres exception name. It reached people
 * as-is because cgErrorMessage falls through to the raw message for anything
 * it does not recognise, and because nothing showed the state until somebody
 * tried to change it and failed.
 *
 * Returns null when there is nothing to say — an order past the gate, or one
 * that never had gate tasks, should carry no badge at all rather than a
 * reassuring green one nobody asked for.
 */
export function gateBadge(
  status: string,
  steps: GateStep[],
  statuses: CgStatus[],
): { label: string; tone: 'warning' } | null {
  if (!atOrBefore(status, GATED_STATUS, statuses) || steps.length === 0 || gateComplete(steps)) {
    return null;
  }
  return { label: 'Awaiting Financial Gate', tone: 'warning' };
}

/**
 * Why the Send PO button is not available yet, or null when it is.
 *
 * A disabled button with no explanation is the thing this whole batch exists
 * to remove, so the reason is always a sentence somebody can act on.
 */
export function sendPoProblem(
  status: string,
  steps: GateStep[],
  /**
   * Whether a supplier_handoffs row points at this order. Undefined while the
   * lookup is still in flight — treated as "no problem yet" rather than as a
   * missing link, so the button does not flicker through a scary sentence on
   * every open. The database refuses either way, so a moment of optimism here
   * cannot let a bad PO through.
   */
  supplierLinked?: boolean,
  /**
   * Whether a signed client certificate exists. Undefined while unknown, on
   * the same reasoning as supplierLinked — the database refuses either way,
   * so a moment of optimism here cannot let an unapproved order through.
   */
  clientApproved?: boolean,
): string | null {
  // Named against BOTH pre-PO statuses. An approved order normally sits on
  // 'Client Approved', but an admin may move a status backwards, and a
  // certificate issued against an order somebody had already dragged back does
  // not re-advance it — so 'Order Placed' is a legitimate place to send a PO
  // from, and cg_send_po_to_supplier accepts it for the same reason.
  if (status !== APPROVAL_STATUS && status !== GATED_STATUS) {
    return `This order is already past the PO stage — it is ${status}.`;
  }
  // Before the gate, deliberately. Somebody missing both a supplier and a paid
  // invoice should be told about the one they can fix in ten seconds, not sent
  // to chase finance and refused again when they come back.
  if (supplierLinked === false) {
    return 'No supplier is linked to this order yet, so there is nobody to send the PO to. Link it to a supplier handoff first.';
  }
  // Then approval, which is the process order as well as roughly the next
  // fastest to fix. This one has NO admin override — unlike the financial gate
  // below it — so saying it plainly matters more than usual: somebody who
  // reads "still to do: invoice" and reaches for the override will be refused
  // again with no explanation of why.
  if (clientApproved === false) {
    return 'The client has not signed this order off yet, so it cannot leave Order Placed. Issue a review link above and send it to them — there is no override for this one.';
  }
  const outstanding = steps.filter((step) => !step.is_completed);
  if (outstanding.length === 0) {
    return null;
  }
  return `Still to do: ${outstanding.map((step) => step.title).join(', ')}.`;
}
